/**
 * API endpoint definitions.
 * Centralized so endpoint paths are never hard-coded in components.
 */

export const endpoints = {
  // ─── Sections ──────────────────────────────────────────────────────────
  sections: {
    list: () => '/sections',
    get: (id: string) => `/sections/${id}`,
    filters: (id: string) => `/sections/${id}/filters`,
  },

  // ─── Analytics (Playground) ────────────────────────────────────────────
  analytics: {
    tables: () => '/analytics/tables',
    events: () => '/analytics/events',
    properties: () => '/analytics/properties',
    numericParams: () => '/analytics/numeric-params',
    timeSeries: () => '/analytics/time-series',
    pivot: () => '/analytics/pivot',
  },

  // ─── Flow (Event Flow + Screen Flow) ────────────────────────────────────
  flow: {
    data: () => '/flow/data',
    continuation: () => '/flow/continuation',
    timeSeries: () => '/flow/time-series',
    events: () => '/flow/events',
  },

  // ─── Reports (sectionId required via query param for get/update/delete) ─
  reports: {
    create: () => '/reports',
    list: (sectionId: string) => `/reports/section/${sectionId}`,
    get: (id: string, sectionId: string) => `/reports/${id}?sectionId=${sectionId}`,
    resolve: (id: string, sectionId: string) => `/reports/${id}/resolve?sectionId=${sectionId}`,
    update: (id: string, sectionId: string) => `/reports/${id}?sectionId=${sectionId}`,
    delete: (id: string, sectionId: string) => `/reports/${id}?sectionId=${sectionId}`,
    duplicate: (id: string, sectionId: string) => `/reports/${id}/duplicate?sectionId=${sectionId}`,
    generateUrl: () => '/reports/url',
  },

  // ─── Folders (sectionId required via query param for update/delete) ────
  folders: {
    create: () => '/reports/folders',
    list: (sectionId: string) => `/reports/folders/${sectionId}`,
    update: (id: string, sectionId: string) => `/reports/folders/${id}?sectionId=${sectionId}`,
    delete: (id: string, sectionId: string) => `/reports/folders/${id}?sectionId=${sectionId}`,
  },

  // ─── Custom Fields (scoped per section) ─────────────────────────────────
  fields: {
    create: (sectionId: string) => `/fields/${sectionId}`,
    list: (sectionId: string) => `/fields/${sectionId}`,
    get: (sectionId: string, id: string) => `/fields/${sectionId}/${id}`,
    update: (sectionId: string, id: string) => `/fields/${sectionId}/${id}`,
    delete: (sectionId: string, id: string) => `/fields/${sectionId}/${id}`,
  },

  // ─── Marketing ──────────────────────────────────────────────────────────
  marketing: {
    pivot: () => '/marketing/pivot',
    timeSeries: () => '/marketing/time-series',
    availableMetrics: () => '/marketing/available-metrics',
    tiers: {
      list: () => '/marketing/tiers',
      create: () => '/marketing/tiers',
      update: (id: string) => `/marketing/tiers/${id}`,
      delete: (id: string) => `/marketing/tiers/${id}`,
    },
    countries: () => '/marketing/countries',
    campaigns: () => '/marketing/campaigns',
    campaignCountries: (name: string) => `/marketing/campaign-countries/${encodeURIComponent(name)}`,
  },

  // ─── Push Notifications ────────────────────────────────────────────────
  push: {
    translate: () => '/push/translate',
    send: () => '/push/send',
    list: () => '/push/list',
    get: (pushId: string) => `/push/${pushId}`,
    delete: (pushId: string) => `/push/${pushId}`,
    deleteAll: () => '/push/all',
    analytics: () => '/push/analytics',
    locales: () => '/push/locales',
  },

  // ─── Users ───────────────────────────────────────────────────────────
  users: {
    list: () => '/users/list',
    summary: () => '/users/summary',
    sessions: () => '/users/sessions',
    sessionEvents: () => '/users/session-events',
  },

  // ─── Purchases ──────────────────────────────────────────────────────────
  purchases: {
    list: () => '/purchases/list',
    summary: () => '/purchases/summary',
    userEvents: () => '/purchases/user-events',
    firstTouch: () => '/purchases/first-touch',
  },

  // ─── Activity ─────────────────────────────────────────────────────────
  activity: {
    list: () => '/activity',
    stats: () => '/activity/stats',
    clear: () => '/activity',
  },

  // ─── Auth ──────────────────────────────────────────────────────────────
  auth: {
    config: () => '/auth/config',
    verify: () => '/auth/verify',
    me: () => '/auth/me',
  },

  // ─── System ────────────────────────────────────────────────────────────
  health: () => '/health',
  cache: {
    stats: () => '/cache/stats',
    clear: () => '/cache/clear',
  },

  // ─── Data refresh (force-run BigQuery scheduled queries) ───────────────
  data: {
    freshness: (section?: string) =>
      section ? `/data/freshness?section=${encodeURIComponent(section)}` : '/data/freshness',
    refresh: () => '/data/refresh',
    refreshStatus: (jobId: string) => `/data/refresh/status?jobId=${encodeURIComponent(jobId)}`,
  },
} as const;
